import { useQuery } from '@apollo/client'
import { useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterContext } from '../components/data-grid/context'
import { AllSitesDocument } from '../generated/graphql'
import { useFormFilter } from '../hooks/form-filter'
import { SelectInput } from '../inputs/select-input'
import { useMe } from '../hooks/me'

export type SiteFilter = {
  siteId?: number
}

export const SiteFilter = () => {
  const { t } = useTranslation(['shared', 'filter'])

  const filterKey = 'siteId'

  const { setFilter, subscribeOnResetFilter, removeFilter, registerFilter } =
    useContext<FilterContext<SiteFilter>>(FilterContext)

  const { me, loading: meLoading } = useMe()
  const defaultSiteId = me?.siteId

  const { data, loading: allSitesLoading } = useQuery(AllSitesDocument)

  const isLoading = meLoading || allSitesLoading

  const options = useMemo(
    () => [
      {
        name: t('filter:site_filter.all_sites'),
        value: null,
      },

      ...[...(data?.allSites || [])].map((site) => ({
        name: `#${site.eshopid} ${site.eshopname}`,
        value: site.eshopid,
      })),
    ],
    [data?.allSites, t],
  )

  const { queryParams, formContext, resetForm } = useFormFilter<SiteFilter>({
    isLoading,
    formProps: {
      defaultValues: { siteId: defaultSiteId },
    },
    parseOptions: {
      types: {
        siteId: (value) => {
          const isAllowedSite = data?.allSites.some(
            ({ eshopid }) => eshopid === Number(value),
          )
          if (isAllowedSite) return Number(value)
        },
      },
    },
  })

  const { siteId } = queryParams

  useEffect(() => {
    registerFilter({ key: filterKey, isReady: !isLoading })
  }, [isLoading, registerFilter])

  useEffect(() => {
    if (!data?.allSites) return

    const currentSiteId = siteId || defaultSiteId
    const label = options.find(({ value }) => value === currentSiteId)?.name

    if (siteId && label) {
      setFilter(filterKey, {
        label: t('filter:site_filter.label'),
        labelValues: [label],
        value: currentSiteId,
        isDefault: currentSiteId === defaultSiteId,
      })
    } else {
      removeFilter(filterKey)
    }
  }, [
    options,
    siteId,
    setFilter,
    t,
    defaultSiteId,
    me,
    data?.allSites,
    removeFilter,
  ])

  useEffect(() => {
    const unsubscribe = subscribeOnResetFilter((key) => {
      if (!key || key === filterKey) {
        resetForm()
      }
    })

    return () => unsubscribe()
  }, [resetForm, subscribeOnResetFilter])

  return (
    <SelectInput
      name="siteId"
      control={formContext.control}
      options={options}
      disabled={isLoading}
      label={t('filter:site_filter.label')}
      slotProps={{ select: { displayEmpty: true } }}
    />
  )
}
